import { useMemo, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import { UseFormReturn } from 'react-hook-form';
import EmojiFlagsIcon from '@mui/icons-material/EmojiFlags';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';

import { Status } from 'helpers/app.config';
import { TextFieldElement } from 'components/muiInputWrappers';
import useSubForm from 'hooks/useSubForm';
import { FormItem, FormTitle } from 'components/shared';

export type FormFields = {
  ticketId?: string;
  clientName?: string;
  clientNumber?: number;
  project?: string;
  jobCategory?: string;
  jobSubCategory?: string;
  descripcionActividades?: string;
  failTypes?: string;
  incidentTypes?: string;
  activitiesToClose?: any;
  taskForceName?: string;
  taskForceEmail?: string;
  dateCreated?: string;
  dateAssigned?: string;
  dateAcepted?: string;
  dateClosed?: string;
  dateExpiration?: string;
  ticketStatusName?: string;
  ticketStatusId?: number;
  ticketFlag?: any;
  ticketProgress?: string;
  ticketStatusColor?: string;
  flags?: any;
  ticketProjectName?: string;
};

type Props<T> = {
  readonly?: boolean;
  form: UseFormReturn<T, any>;
  name: string;
  isLoading?: boolean;
  showTextArea?: boolean;
  showfailTypes?: boolean;
  showFolio?: boolean;
  showClientNumber?: boolean;
  showClient?: boolean;
  showProject?: boolean;
  showServiceType?: boolean;
  showJobSubCategory?: boolean;
  showIncident?: boolean;
  showActivitiesToClose?: boolean;
  showTaskForce?: boolean;
  showDateCreated?: boolean;
  showDateAssigned?: boolean;
  showDateAcepted?: boolean;
  showDateClosed?: boolean;
  showDateExpiration?: boolean;
  titleTaskForce?: string; // *
  columns?: 2 | 4;
  showTicketStatus?: boolean;
  showTicketFlag?: boolean;
  showTicketProgress?: boolean;
};

/**
 * TODO
 * @param props
 * @returns
 */

export function InfoTicket<T>({
  form,
  name,
  readonly = true,
  showTextArea = true,
  showfailTypes = false,
  isLoading = false,
  showFolio = true,
  showClient = true,
  showClientNumber = false,
  showProject = true,
  showServiceType = false,
  showJobSubCategory = false,
  showIncident = false,
  showActivitiesToClose = false,
  titleTaskForce = 'Fuerza de Trabajo',
  showTaskForce,
  showDateCreated,
  showDateAssigned,
  showDateClosed,
  showDateExpiration,
  showTicketStatus,
  showTicketProgress,
  columns = 2
}: Props<T>) {
  const { control, getName, getValues, watch } = useSubForm<FormFields>({
    form,
    name
  });

  /* TABLE */
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.common.black,
      color: theme.palette.common.white
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14
    }
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover
    },
    // hide last border
    '&:last-child td, &:last-child th': {
      border: 0
    }
  }));

  function createData(name: any, percent: any) {
    return { name, percent };
  }

  const ticketsFlags = watch(getName('flags'));
  const ticketStatusId = getValues(getName('ticketStatusId'));

  const [ticketsFlagsTotalPercent, setTicketsFlagsTotalPercent] = useState(0);
  let ticketsFlagsTotalPercentVal = 0;

  const rows = useMemo(() => {
    const ticketsFlagsKeys: any[] = ticketsFlags
      ? Object.keys(ticketsFlags)
      : [];
    const ticketsFlagsValues: any[] = ticketsFlags
      ? Object.values(ticketsFlags)
      : [];

    return ticketsFlagsKeys?.map((key: any, i) => {
      const currentStatusName = Status[ticketsFlagsValues[i].statusId].name;
      ticketsFlagsTotalPercentVal += ticketsFlagsValues[i].percent;
      setTicketsFlagsTotalPercent(ticketsFlagsTotalPercentVal);
      return createData(currentStatusName, ticketsFlagsValues[i].percent);
    });
  }, [ticketsFlags]);

  /* TABLE */

  const { t } = useTranslation('global');

  return (
    <Grid spacing={2} container>
      {showFolio && (
        <Grid lg={12 / columns} md={12} sm={12} xs={12} item>
          <FormTitle>Folio</FormTitle>
          <FormItem>
            <TextFieldElement
              control={control}
              disabled={readonly}
              name={getName('ticketId')}
              placeholder={isLoading ? 'Cargando...' : undefined}
              size="small"
              variant="outlined"
              fullWidth
            />
          </FormItem>
        </Grid>
      )}

      {showClientNumber && (
        <Grid lg={12 / columns} md={12} sm={12} xs={12} item>
          <FormTitle>n.º Incidente del Cliente</FormTitle>
          <FormItem>
            <TextFieldElement
              control={control}
              disabled={readonly}
              name={getName('clientNumber')}
              placeholder={
                isLoading ? 'Cargando...' : 'No. Incidente del Cliente'
              }
              size="small"
              variant="outlined"
              fullWidth
            />
          </FormItem>
        </Grid>
      )}

      {showfailTypes && (
        <Grid lg={12 / columns} md={12} sm={12} xs={12} item>
          <FormTitle>Tipo de Falla</FormTitle>
          <FormItem>
            <TextFieldElement
              control={control}
              disabled={readonly}
              name={getName('failTypes')}
              placeholder={isLoading ? 'Cargando...' : 'Tipo de Falla'}
              size="small"
              variant="outlined"
              fullWidth
            />
          </FormItem>
        </Grid>
      )}

      {showClient && (
        <Grid lg={12 / columns} md={12} sm={12} xs={12} item>
          <FormTitle>{t('common.commonProperties.client')}</FormTitle>
          <FormItem>
            <TextFieldElement
              control={control}
              disabled={readonly}
              name={getName('clientName')}
              placeholder={isLoading ? 'Cargando...' : undefined}
              size="small"
              variant="outlined"
              fullWidth
            />
          </FormItem>
        </Grid>
      )}

      {showProject && (
        <Grid lg={12 / columns} md={12} sm={12} xs={12} item>
          <FormTitle>Tipo de servicio</FormTitle>
          <FormItem>
            <TextFieldElement
              control={control}
              disabled={readonly}
              name={getName('project')}
              placeholder={isLoading ? 'Cargando...' : undefined}
              size="small"
              variant="outlined"
              fullWidth
            />
          </FormItem>
        </Grid>
      )}

      {showServiceType && (
        <Grid lg={12 / columns} md={12} sm={12} xs={12} item>
          <FormTitle>Nombre del Servicio</FormTitle>
          <FormItem>
            <TextFieldElement
              control={control}
              disabled={readonly}
              name={getName('ticketProjectName')}
              placeholder={isLoading ? 'Cargando...' : undefined}
              size="small"
              variant="outlined"
              fullWidth
            />
          </FormItem>
        </Grid>
      )}

      {showJobSubCategory && (
        <Grid lg={12 / columns} md={12} sm={12} xs={12} item>
          <FormTitle>Tipo de Servicio</FormTitle>
          <FormItem>
            <TextFieldElement
              control={control}
              disabled={readonly}
              name={getName('jobSubCategory')}
              placeholder={isLoading ? 'Cargando...' : 'Nombre del Servicio'}
              size="small"
              variant="outlined"
              fullWidth
            />
          </FormItem>
        </Grid>
      )}

      {showIncident && (
        <Grid lg={12 / columns} md={12} sm={12} xs={12} item>
          <FormTitle>Nombre de Servicio</FormTitle>
          <FormItem>
            <TextFieldElement
              control={control}
              disabled={readonly}
              name={getName('incidentTypes')}
              placeholder={isLoading ? 'Cargando...' : 'Tipo de Incidente'}
              size="small"
              variant="outlined"
              fullWidth
            />
          </FormItem>
        </Grid>
      )}

      {showTaskForce && (
        <Grid lg={12 / columns} md={12} sm={12} xs={12} item>
          <FormTitle gutterBottom>{titleTaskForce}</FormTitle>
          <FormItem>
            <TextFieldElement
              control={control}
              disabled={readonly}
              name={getName('taskForceName')}
              placeholder={isLoading ? 'Cargando...' : null}
              size="small"
              variant="outlined"
              fullWidth
            />
          </FormItem>
        </Grid>
      )}

      {showDateCreated && (
        <Grid lg={12 / columns} md={12} sm={12} xs={12} item>
          <FormTitle gutterBottom>Creación de Ticket</FormTitle>
          <FormItem>
            <TextFieldElement
              control={control}
              disabled={readonly}
              name={getName('dateCreated')}
              placeholder={isLoading ? 'Cargando...' : null}
              size="small"
              variant="outlined"
              fullWidth
            />
          </FormItem>
        </Grid>
      )}

      {showDateAssigned && (
        <Grid lg={12 / columns} md={12} sm={12} xs={12} item>
          <FormTitle gutterBottom>Asignación de Ticket</FormTitle>
          <FormItem>
            <TextFieldElement
              control={control}
              disabled={readonly}
              name={getName('dateAssigned')}
              placeholder={isLoading ? 'Cargando...' : null}
              size="small"
              variant="outlined"
              fullWidth
            />
          </FormItem>
        </Grid>
      )}

      {showDateExpiration && (
        <Grid lg={12 / columns} md={12} sm={12} xs={12} item>
          <FormTitle gutterBottom>Vencimiento de Ticket</FormTitle>
          <FormItem>
            <TextFieldElement
              control={control}
              disabled={readonly}
              name={getName('dateExpiration')}
              placeholder={isLoading ? 'Cargando...' : null}
              size="small"
              variant="outlined"
              fullWidth
            />
          </FormItem>
        </Grid>
      )}

      {showDateClosed && ticketStatusId === 5 && (
        <Grid lg={12 / columns} md={12} sm={12} xs={12} item>
          <FormTitle gutterBottom>Cierre de Ticket</FormTitle>
          <FormItem>
            <TextFieldElement
              control={control}
              disabled={readonly}
              name={getName('dateClosed')}
              placeholder={isLoading ? 'Cargando...' : null}
              size="small"
              variant="outlined"
              fullWidth
            />
          </FormItem>
        </Grid>
      )}

      {showTicketProgress && (
        <Grid lg={4} md={12} sm={12} xs={12} item>
          <FormTitle gutterBottom>Bandera de Seguimiento</FormTitle>

          {isLoading ? (
            <CircularProgress size={20} />
          ) : (
            <FormItem>
              <TextFieldElement
                control={control}
                disabled={readonly}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmojiFlagsIcon
                        sx={{
                          color: getValues(getName('ticketStatusColor'))
                            ? getValues(getName('ticketStatusColor')) ||
                              '#000000'
                            : '#000000'
                        }}
                      />
                    </InputAdornment>
                  )
                }}
                name={getName('ticketProgress')}
                placeholder={isLoading ? 'Cargando...' : null}
                size="small"
                variant="outlined"
                fullWidth
              />
            </FormItem>
          )}
        </Grid>
      )}

      {showTicketStatus && (
        <Grid lg={4} md={12} sm={12} xs={12} item>
          <FormTitle gutterBottom>&nbsp;</FormTitle>
          <FormItem>
            <TextFieldElement
              control={control}
              disabled={readonly}
              name={getName('ticketStatusName')}
              placeholder={isLoading ? 'Cargando...' : null}
              size="small"
              variant="outlined"
              fullWidth
            />
          </FormItem>
        </Grid>
      )}

      {/* Mostrar TABLE */}
      {showTicketProgress && (
        <Grid lg={6} md={12} sm={12} xs={12} item>
          <FormTitle gutterBottom>Ciclo de Vida</FormTitle>
          <FormItem>
            {/* TABLE */}
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>CICLO DE VIDA</StyledTableCell>
                    <StyledTableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <StyledTableRow key={row.name}>
                      <StyledTableCell component="th" scope="row">
                        {row.name}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {`${parseFloat(row.percent).toFixed(2)}%`}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                  <TableRow>
                    <StyledTableCell>Progreso Total</StyledTableCell>
                    <StyledTableCell
                      align="right"
                      sx={{ backgroundColor: '#000000', color: '#ffffff' }}
                    >
                      {`${ticketsFlagsTotalPercent.toFixed(2)}%`}
                    </StyledTableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            {/* TABLE */}
          </FormItem>
        </Grid>
      )}
      {/* Mostrar TABLE */}

      {showTextArea && (
        <Grid xs={12} item>
          <FormTitle>Descripción de Actividades a Realizar</FormTitle>
          <FormItem>
            <TextFieldElement
              control={control}
              disabled={readonly}
              name={getName('descripcionActividades')}
              placeholder={
                isLoading ? 'Cargando...' : 'Descripción de Actividades'
              }
              rows={4}
              size="small"
              variant="outlined"
              fullWidth
              multiline
            />
          </FormItem>
        </Grid>
      )}

      {showActivitiesToClose && (
        <Grid xs={12} item>
          <FormTitle>Descripción de Actividades Realizadas en Sitio</FormTitle>
          <FormItem>
            <TextFieldElement
              control={control}
              disabled={readonly}
              name={getName('activitiesToClose')}
              placeholder={
                isLoading ? 'Cargando...' : 'Descripción de Actividades'
              }
              rows={4}
              size="small"
              variant="outlined"
              fullWidth
              multiline
            />
          </FormItem>
        </Grid>
      )}
    </Grid>
  );
}

export default InfoTicket;
